import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { CustomTable } from 'common/CustomTable/CustomTable';
import { Paper } from '@mui/material'
import { TableTitle, useNoSearchResultsToast } from '../../common'
import { useExpanded } from 'components/layouts/ProtectedLayout';
import { useBatchPrint, useSetConfig } from "hooks"
import PrintIcon from '@mui/icons-material/Print';
import { selectAllReducer } from './helpers';
import { PrintModal } from './components'
import { SelectAll } from 'interfaces/pages/components';
import { pageStyles } from 'customStyles/common';
import { useTableStore } from 'state/TableStore';
import { useLocation } from 'react-router-dom';
import { extractPageName } from 'common/CustomTable/helpers';
import { useUserStore } from 'state';
import { TableNames } from 'customEnums/TableNames';

export const BatchPrinting: React.FC = () => {
    const { expanded } = useExpanded()
    const [value, setValue] = React.useState<any>([]);
    const [searched, setSearched] = useState(false)
    const setHeaderButtonConfig = useTableStore(state => state.setHeaderButtonConfig)
    const tcns = useTableStore(state => state.tcns)
    const setTcns = useTableStore(state => state.setTcns)
    const selected = useTableStore(state => state.selected)
    const setSelected = useTableStore(state => state.setSelected)
    const [openPrint, setOpenPrint] = useState(false)
    const simpleSearchRan = useTableStore((state) => state.simpleSearchRan)
    const setPageName = useTableStore(state => state.setPageName)


    const initialSelectAllState = {
        pages: [{ page: 1, selectAll: false }] as SelectAll[]
    }
    const [selectAllState, dispatchSelectAll] = useReducer(selectAllReducer, initialSelectAllState)
    const userConfig = useUserStore(state => state.userConfig)
    const { usersPermissions } = userConfig;

    const searchParams = useTableStore((state) => state.searchParams)
    const setDisplayNoSearchResults = useTableStore(state => state.setDisplayNoSearchResults)
    const locationObj = useLocation()
    const pageName = extractPageName(locationObj.pathname)
    useNoSearchResultsToast();

    const tableConfig = useTableStore(state => state.tableConfig)

    const { tableHeaderCells, page, rowsPerPage } = useSetConfig(pageName, { page: 1, rowsPerPage: 25 }, TableNames.BATCH_PRINTING, {})


    const handleOpenPrint = useCallback(() => {
        console.log('batch print flow handleOpenPrint')
        setOpenPrint(true)
    }, [])

    const batchPrintHeaderButtonConfig = useMemo(() => {
        return {
            handler: handleOpenPrint,
            headerButtonIcon: <PrintIcon />,
            buttonLabel: "Print-Records",
            disabled: true,
            text: "Print",
            displayButton: true
        }
    }, [handleOpenPrint])

    const setSearchParams = useTableStore((state) => state.setSearchParams)
    const setSimpleSearchRan = useTableStore((state) => state.setSimpleSearchRan)

    // Clear search params when mounting this page
    useEffect(() => {
        setPageName('batch-printing');
        console.log('BatchPrinting mounting - clearing search params');
        setSearchParams([]);
        setSimpleSearchRan(false);

        return () => {
            console.log('BatchPrinting unmounting - clearing selections');
            setSelected([]);
            setTcns([]);
        };
    }, [
        setPageName,
        setSearchParams,
        setSimpleSearchRan,
        setSelected,
        setTcns
    ]);

    useEffect(() => {
        const hasSelection = tcns.length > 0
        setHeaderButtonConfig({ ...batchPrintHeaderButtonConfig, disabled: !hasSelection })
        console.log(`debug visibleRows page ${page}, rowsPerPage: ${rowsPerPage}`)
    }, [batchPrintHeaderButtonConfig, page, rowsPerPage, setHeaderButtonConfig, tcns.length])

    const {
        data: mappedData,
        totalRecords,
        loading
    } = useBatchPrint(rowsPerPage, page, searchParams)

    useEffect(() => {
        console.log('batch print flow selected udpated: ', selected)
        console.log(`batch print flow mappedData: `, mappedData)
    }, [selected, mappedData])

    useEffect(() => {
        if (mappedData.length === 0 && simpleSearchRan) setDisplayNoSearchResults(true)
    }, [mappedData.length, simpleSearchRan, setDisplayNoSearchResults])

    const handleClose = () => {
        setOpenPrint(false)
        setSelected([])
        setTcns([])
        dispatchSelectAll({ type: "DESELECT_ALL", page: 1 })
    }

    const handleDeselectAll = () => {
        setTcns([]);
        setSelected([]);
    }

    const handleRowClick = (e: any, row?: any) => {
        console.log(`debug checkbox batch print flow handleRowClick: `, { row })
        handlePrintRecordSelect({ event: e, tcn: row.transactionNumber, id: row.key });
        handleClick(e, row.transactionNumber)
    }


    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: string[] = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('batch print flow handleSelectAll selecting all tcns: ')
        console.log(`select all pagination current page: ${page}`)
        console.log(`select all pagination visible`)
        if (e.target.checked) {
            dispatchSelectAll({ type: "SELECT_ALL", page })
            const currentPageRecords = mappedData.map((record: any) => record)
            const merged = [
                ...tcns.filter((t: any) =>
                    !currentPageRecords.some((r: any) => r.transactionNumber === t.transactionNumber)
                ),
                ...currentPageRecords
            ]
            console.log('batch print flow selecting all: ', merged)
            setTcns(merged)
        } else {
            dispatchSelectAll({ type: "DESELECT_ALL", page })
            const currentPageTcns = new Set(mappedData.map((r: any) => r.transactionNumber))
            const filtered = tcns.filter((t: any) => !currentPageTcns.has(t.transactionNumber))
            console.log('batch-printing tcns handleSelectAll deSelecting all tcns')
            setTcns(filtered)
        }
        console.log('batch print flow final tcns handleSelectAll done')
    }

    const isCurrentPageAllSelected = useMemo(() => {
        if (mappedData.length === 0) return false
        return mappedData.every((row: any) =>
            tcns.some((t: any) => t.transactionNumber === row.transactionNumber)
        )
    }, [mappedData, tcns])

    const isCurrentPagePartiallySelected = useMemo(() => {
        if (mappedData.length === 0) return false
        const someSelected = mappedData.some((row: any) => tcns.some((t: any) => t.transactionNumber === row.transactionNumber))
        return someSelected && !isCurrentPageAllSelected
    }, [mappedData, tcns, isCurrentPageAllSelected])

    const handlePrintRecordSelect = (params: any) => {
        const { event, tcn } = params

        console.log(`debug checkbox batch print flow handlePrintRecordSelect params: `, { params })

        event.stopPropagation()

        if (tcns.some((row: any) => row.transactionNumber === tcn)) {
            console.log(`batch print flow handlePrintRecordSelect deleting tcn: ${tcn}`)
            const updated = tcns.filter((item: any) => item.transactionNumber !== tcn)
            console.log(`batch print flow handlePrintRecordSelect updated list: ${JSON.stringify(updated, null, 2)}`)
            setTcns(updated)
        } else {
            const match = mappedData.find((row: any) => row.transactionNumber === tcn)
            if (match) {
                console.log(`batch print flow handlePrintRecordSelect adding tcn: ${tcn}`)
                setTcns([...tcns, match])
            }
        }
    }

    return (
        <>
            {
                usersPermissions?.viewBatchPrinting
                    ?
                    <Paper elevation={0} sx={{ ...pageStyles.pagePaperStyle, minWidth: `calc(100vw - ${expanded ? "256px" : "64px"})` }}>
                        <TableTitle name="Batch Printing" />
                        {
                            tableHeaderCells &&
                            <CustomTable
                                tableConfig={tableConfig}
                                tableName="batch-printing"
                                loading={loading}
                                rows={mappedData}
                                totalRecords={totalRecords}
                                currentPage={page}
                                pageSize={rowsPerPage}
                                searched={searched}
                                value={value}
                                setValue={setValue}
                                setSearched={setSearched}
                                handleDeselectAll={handleDeselectAll}
                                //Per page props
                                tcns={tcns}
                                headerButtonHandler={handleOpenPrint}
                                headerButtonText="Print"
                                headerButtonIcon={<PrintIcon />}
                                headerButtonAriaLabel="print-record(s)"
                                handleTableItemSelect={handlePrintRecordSelect}
                                handleSelectAll={handleSelectAll}
                                savedSearchState={[{}, () => { }]}
                                selected={selected}
                                setSelected={setSelected}
                                selectAllState={selectAllState}
                                rowClickHandler={(e, row) => handleRowClick(e, row)}
                                isCurrentPageAllSelected={isCurrentPageAllSelected}
                                isCurrentPagePartiallySelected={isCurrentPagePartiallySelected}
                            />
                        }

                        {openPrint && (
                            <PrintModal
                                totalRecords={totalRecords}
                                label="batch-printing"
                                handleOpenPrint={handleOpenPrint}
                                rows={mappedData}
                                handleClose={handleClose}
                                open={openPrint}
                            />
                        )
                        }
                    </Paper>
                    :
                    null
            }
        </>
    )
}
